<script setup lang="ts">
/**
 * SG Incorporation (Post) — Word 템플릿 13종을 업무 흐름 Stage 로 구성.
 *
 * Stage 1  Welcome & First Board
 * Stage 2  Share Capital & Applications (Individual / Corporate 병행·복수)
 * Stage 3  CSP Engagement & Indemnity
 * Stage 4  Nominee Director (optional include/exclude)
 * Stage 5  Director Disclosure & Financial Year
 */
import { ref, computed, watch, provide } from 'vue';
import type { Component } from 'vue';
import DocScope from '../incorporation-pre/cdd/DocScope.vue';
import { useCddFill } from '../incorporation-pre/cdd/useCddFill';
import SgWelcomeLetterDoc from './SgWelcomeLetterDoc.vue';
import SgFirstDirectorsResolutionDoc from './SgFirstDirectorsResolutionDoc.vue';
import SgFormA1Doc from './SgFormA1Doc.vue';
import SgShareApplicationIndividualDoc from './SgShareApplicationIndividualDoc.vue';
import SgShareApplicationCorporateDoc from './SgShareApplicationCorporateDoc.vue';
import SgPaidUpCapitalLetterDoc from './SgPaidUpCapitalLetterDoc.vue';
import SgCorporateSecretarialAgreementDoc from './SgCorporateSecretarialAgreementDoc.vue';
import SgRegisteredAddressAgreementDoc from './SgRegisteredAddressAgreementDoc.vue';
import SgNominationLetterDoc from './SgNominationLetterDoc.vue';
import SgLetterOfIndemnityDoc from './SgLetterOfIndemnityDoc.vue';
import SgNomineeDirectorAgreementDoc from './SgNomineeDirectorAgreementDoc.vue';
import SgDdfDoc from './SgDdfDoc.vue';
import SgFinancialYearChecklistDoc from './SgFinancialYearChecklistDoc.vue';
import type { SgPostChildLocal } from './sgPostChildLocal';

/** Child docs (First Res / Indemnity / Welcome) sync row ids from this bag */
const childLocal = ref<SgPostChildLocal>({
  firstResDirectorIds: [1],
  firstResAllotteeIds: [1],
  indemnityIds: [1],
  welcomeContactIds: [1],
});
provide('sgIncPostChildLocal', childLocal);

interface SlotDoc {
  key: string;
  icon: string;
  name: string;
  comp: Component;
  no?: number;
  scope?: string;
  id?: number;
  listKey?: 'shareInd' | 'shareCorp' | 'ddf';
}

interface StageView {
  no: number;
  en: string;
  docs: SlotDoc[];
}

const currentStage = ref(1);
const includeNominee = ref(false);
/** Paid-up capital confirmation — optional (default off) */
const includePaidUp = ref(false);

/** Stage 2 — 개인/법인 Share Application (기본 0부, 필요할 때 추가) */
const shareIndIds = ref<number[]>([]);
const shareCorpIds = ref<number[]>([]);
let shareIndSeq = 0;
let shareCorpSeq = 0;

function seedShareCompany(scope: string) {
  try {
    const fill = useCddFill();
    const company = String(fill.values.company_name || '').trim();
    const addr = String(fill.values.company_registered_address || fill.values.registered_office_address || '').trim();
    if (company) fill.setValue(`${scope}company_name`, company);
    if (addr) fill.setValue(`${scope}company_registered_address`, addr);
    if (!fill.values[`${scope}currency_symbol`]) fill.setValue(`${scope}currency_symbol`, 'S$');
  } catch {
    /* fill not provided */
  }
}

function addShareInd() {
  const id = ++shareIndSeq;
  shareIndIds.value.push(id);
  seedShareCompany(`share_ind_${id}/`);
}
function addShareCorp() {
  const id = ++shareCorpSeq;
  shareCorpIds.value.push(id);
  seedShareCompany(`share_corp_${id}/`);
}
function removeShareInd(id: number) {
  shareIndIds.value = shareIndIds.value.filter((x) => x !== id);
}
function removeShareCorp(id: number) {
  shareCorpIds.value = shareCorpIds.value.filter((x) => x !== id);
}

/** Stage 5 — DDF N부 */
const ddfIds = ref<number[]>([1]);
let ddfSeq = 1;
function addDdf() { ddfIds.value.push(++ddfSeq); }
function removeDdf(id: number) {
  if (ddfIds.value.length <= 1) return;
  ddfIds.value = ddfIds.value.filter((x) => x !== id);
}

function removeCopy(listKey: 'shareInd' | 'shareCorp' | 'ddf', id: number) {
  if (listKey === 'shareInd') removeShareInd(id);
  else if (listKey === 'shareCorp') removeShareCorp(id);
  else removeDdf(id);
}

function canRemove(listKey: 'shareInd' | 'shareCorp' | 'ddf') {
  if (listKey === 'shareInd') return shareIndIds.value.length >= 1;
  if (listKey === 'shareCorp') return shareCorpIds.value.length >= 1;
  return ddfIds.value.length > 1;
}

const view = computed<StageView[]>(() => {
  let n = 0;
  const stages: StageView[] = [
    {
      no: 1,
      en: 'Welcome & First Board',
      docs: [
        { key: 'welcome', icon: '👋', name: 'Welcome Letter', comp: SgWelcomeLetterDoc, no: (n += 1) },
        { key: 'first_res', icon: '📝', name: "First Directors' Resolution", comp: SgFirstDirectorsResolutionDoc, no: (n += 1) },
        { key: 'form_a1', icon: '📋', name: 'Form A1 — Register of Controllers / Nominee Directors', comp: SgFormA1Doc, no: (n += 1) },
      ],
    },
    {
      no: 2,
      en: 'Share Capital & Applications',
      docs: [
        ...shareIndIds.value.map((id) => ({
          key: 'share_ind',
          icon: '🙍',
          name: 'Share Application Form — Individual',
          comp: SgShareApplicationIndividualDoc,
          no: (n += 1),
          id,
          scope: `share_ind_${id}/`,
          listKey: 'shareInd' as const,
        })),
        ...shareCorpIds.value.map((id) => ({
          key: 'share_corp',
          icon: '🏢',
          name: 'Share Application Form — Corporate',
          comp: SgShareApplicationCorporateDoc,
          no: (n += 1),
          id,
          scope: `share_corp_${id}/`,
          listKey: 'shareCorp' as const,
        })),
        ...(includePaidUp.value
          ? [{
              key: 'paid_up',
              icon: '💰',
              name: 'Letter of Confirmation of Paid Up Capital',
              comp: SgPaidUpCapitalLetterDoc,
              no: (n += 1),
            }]
          : []),
      ],
    },
    {
      no: 3,
      en: 'CSP Engagement & Indemnity',
      docs: [
        { key: 'cosec_agr', icon: '📑', name: 'Corporate Secretarial Service Agreement', comp: SgCorporateSecretarialAgreementDoc, no: (n += 1) },
        { key: 'reg_addr', icon: '📍', name: 'Registered Address Service Agreement (+ Annexure B)', comp: SgRegisteredAddressAgreementDoc, no: (n += 1) },
        { key: 'nomination', icon: '✉️', name: 'Nomination Letter', comp: SgNominationLetterDoc, no: (n += 1) },
        { key: 'indemnity', icon: '🛡️', name: 'Letter of Indemnity (Company Secretary)', comp: SgLetterOfIndemnityDoc, no: (n += 1) },
      ],
    },
    {
      no: 4,
      en: 'Nominee Director',
      docs: includeNominee.value
        ? [{
            key: 'nominee',
            icon: '👤',
            name: 'Nominee Resident Director Agreement',
            comp: SgNomineeDirectorAgreementDoc,
            no: (n += 1),
          }]
        : [],
    },
    {
      no: 5,
      en: 'Director Disclosure & Financial Year',
      docs: [
        ...ddfIds.value.map((id) => ({
          key: 'ddf',
          icon: '📢',
          name: "DDF — Disclosure of Director's Interest (s.156 / s.165)",
          comp: SgDdfDoc,
          no: (n += 1),
          id,
          scope: `ddf_${id}/`,
          listKey: 'ddf' as const,
        })),
        { key: 'fy_check', icon: '📅', name: 'Checklist for Financial Year', comp: SgFinancialYearChecklistDoc, no: (n += 1) },
      ],
    },
  ];

  return stages;
});

watch(view, (stages) => {
  if (!stages.length) { currentStage.value = 1; return; }
  if (!stages.some((s) => s.no === currentStage.value)) currentStage.value = stages[0].no;
}, { immediate: true });

const stageIndex = computed(() => view.value.findIndex((s) => s.no === currentStage.value));
const totalStages = computed(() => view.value.length);
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));

function go(n: number) { if (view.value.some((s) => s.no === n)) currentStage.value = n; }
function goPrev() { const i = stageIndex.value; if (i > 0) currentStage.value = view.value[i - 1].no; }
function goNext() {
  const i = stageIndex.value;
  if (i >= 0 && i < view.value.length - 1) currentStage.value = view.value[i + 1].no;
}

function gatherLocalState() {
  return {
    shareIndIds: [...shareIndIds.value],
    shareCorpIds: [...shareCorpIds.value],
    ddfIds: [...ddfIds.value],
    includeNominee: includeNominee.value,
    includePaidUp: includePaidUp.value,
    currentStage: currentStage.value,
    firstResDirectorIds: [...childLocal.value.firstResDirectorIds],
    firstResAllotteeIds: [...childLocal.value.firstResAllotteeIds],
    indemnityIds: [...childLocal.value.indemnityIds],
    welcomeContactIds: [...childLocal.value.welcomeContactIds],
  };
}

function restoreLocalState(state: Record<string, any> | null | undefined) {
  if (!state) return;
  if (Array.isArray(state.shareIndIds)) {
    shareIndIds.value = state.shareIndIds.length ? [...state.shareIndIds] : [];
    shareIndSeq = Math.max(shareIndSeq, ...shareIndIds.value, 0);
  }
  if (Array.isArray(state.shareCorpIds)) {
    shareCorpIds.value = [...state.shareCorpIds];
    shareCorpSeq = Math.max(shareCorpSeq, ...shareCorpIds.value, 0);
  }
  if (Array.isArray(state.ddfIds) && state.ddfIds.length) {
    ddfIds.value = [...state.ddfIds];
    ddfSeq = Math.max(ddfSeq, ...ddfIds.value);
  }
  if (typeof state.includeNominee === 'boolean') includeNominee.value = state.includeNominee;
  if (typeof state.includePaidUp === 'boolean') includePaidUp.value = state.includePaidUp;
  if (state.currentStage) currentStage.value = Number(state.currentStage) || 1;
  childLocal.value = {
    firstResDirectorIds: Array.isArray(state.firstResDirectorIds) && state.firstResDirectorIds.length
      ? [...state.firstResDirectorIds]
      : [1],
    firstResAllotteeIds: Array.isArray(state.firstResAllotteeIds) && state.firstResAllotteeIds.length
      ? [...state.firstResAllotteeIds]
      : [1],
    indemnityIds: Array.isArray(state.indemnityIds) && state.indemnityIds.length
      ? [...state.indemnityIds]
      : [1],
    welcomeContactIds: Array.isArray(state.welcomeContactIds) && state.welcomeContactIds.length
      ? [...state.welcomeContactIds]
      : [1],
  };
}

defineExpose({ gatherLocalState, restoreLocalState });

// ─── 문서 확대 (시니어 가독성) — localStorage 유지 ───────────────
const ZOOM_KEY = 'sgIncPost.docZoom';
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
function clampZoom(n: number) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}
function readStoredZoom(): number {
  try {
    const n = Number(localStorage.getItem(ZOOM_KEY));
    if (Number.isFinite(n)) return clampZoom(n);
  } catch {
    /* ignore */
  }
  return 100;
}
const docZoom = ref(readStoredZoom());
const canZoomIn = computed(() => docZoom.value < ZOOM_MAX);
const canZoomOut = computed(() => docZoom.value > ZOOM_MIN);
function setDocZoom(n: number) {
  docZoom.value = clampZoom(n);
  try {
    localStorage.setItem(ZOOM_KEY, String(docZoom.value));
  } catch {
    /* ignore */
  }
}
function zoomIn() {
  setDocZoom(docZoom.value + ZOOM_STEP);
}
function zoomOut() {
  setDocZoom(docZoom.value - ZOOM_STEP);
}
function zoomReset() {
  setDocZoom(100);
}
</script>

<template>
  <div class="allot-pkg sg-inc-post-pkg">
    <div class="stepper">
      <button
        v-for="(st, idx) in view" :key="st.no"
        class="step" :class="{ active: currentStage === st.no, done: stageIndex > idx }"
        :title="`Stage ${st.no} · ${st.en}`"
        @click="go(st.no)"
      >
        <span class="step-no">{{ stageIndex > idx ? '✓' : (idx + 1) }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.en }}</small></span>
      </button>
    </div>

    <!-- Document zoom — separate row (not scaled with paper) -->
    <div class="zoom-bar">
      <div class="doc-zoom" role="group" aria-label="Larger text">
        <button
          type="button"
          class="doc-zoom-btn"
          :disabled="!canZoomOut"
          title="Smaller text"
          @click="zoomOut"
        >
          A−
        </button>
        <button type="button" class="doc-zoom-pct" title="Reset size" @click="zoomReset">
          {{ docZoom }}%
        </button>
        <button
          type="button"
          class="doc-zoom-btn"
          :disabled="!canZoomIn"
          title="Larger text"
          @click="zoomIn"
        >
          A+
        </button>
      </div>
    </div>

    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
    <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
      <div class="stage-head">
        <span class="stage-badge">Stage {{ st.no }}</span>
        <span class="stage-t">{{ st.en }}</span>
      </div>

      <div v-if="st.no === 2" class="opt-panel">
        <div class="opt-row">
          <div class="opt-meta">
            <div class="opt-title">Share Application</div>
            <div class="opt-desc">Optional — add only when allotting new shares</div>
          </div>
          <div class="opt-actions">
            <button type="button" class="sg-tool" @click="addShareInd">+ Individual</button>
            <button type="button" class="sg-tool" @click="addShareCorp">+ Corporate</button>
            <span class="opt-count">{{ shareIndIds.length + shareCorpIds.length }} added</span>
          </div>
        </div>
        <div class="opt-row">
          <div class="opt-meta">
            <div class="opt-title">Paid-up Capital Letter</div>
            <div class="opt-desc">Optional — confirm capital injection after bank account opens</div>
          </div>
          <div class="opt-actions">
            <button
              v-if="!includePaidUp"
              type="button"
              class="sg-tool"
              @click="includePaidUp = true"
            >+ Include</button>
            <template v-else>
              <span class="opt-on">Included</span>
              <button type="button" class="sg-tool muted" @click="includePaidUp = false">Remove</button>
            </template>
          </div>
        </div>
      </div>

      <div v-if="st.no === 4" class="opt-panel">
        <div class="opt-row">
          <div class="opt-meta">
            <div class="opt-title">Nominee Director Agreement</div>
            <div class="opt-desc">Optional — include when appointing a nominee resident director</div>
          </div>
          <div class="opt-actions">
            <button
              v-if="!includeNominee"
              type="button"
              class="sg-tool"
              @click="includeNominee = true"
            >+ Include</button>
            <template v-else>
              <span class="opt-on">Included</span>
              <button type="button" class="sg-tool muted" @click="includeNominee = false">Remove</button>
            </template>
          </div>
        </div>
      </div>

      <div v-if="st.no === 5" class="sg-toolbar">
        <span class="sg-toolbar-lbl">Add document copies</span>
        <button type="button" class="sg-tool" @click="addDdf">+ DDF (Director Disclosure)</button>
      </div>

      <div
        v-if="st.no === 2 && !shareIndIds.length && !shareCorpIds.length && !includePaidUp"
        class="empty-hint"
      >
        No optional documents in this stage yet. Use the controls above to add them.
      </div>
      <div
        v-else-if="st.no === 2 && !shareIndIds.length && !shareCorpIds.length"
        class="empty-hint soft"
      >
        No share applications yet. Add Individual or Corporate above if needed.
      </div>
      <div
        v-else-if="st.no === 4 && !includeNominee"
        class="empty-hint soft"
      >
        Nominee Director Agreement is not included. Use + Include above if needed.
      </div>

      <div v-for="d in st.docs" :key="d.scope || d.key" class="pkg-doc">
        <div class="pkg-doclabel">
          <span class="ic">{{ d.icon }}</span>
          <span class="nm">{{ d.name }}</span>
          <span v-if="d.id" class="copy">#{{ d.id }}</span>
          <button
            v-if="d.listKey && d.id && canRemove(d.listKey)"
            type="button"
            class="sg-rm"
            title="Remove copy"
            @click="removeCopy(d.listKey, d.id)"
          >&times;</button>
          <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
        </div>
        <div class="simple-frame">
          <DocScope v-if="d.scope" :scope="d.scope">
            <component :is="d.comp" />
          </DocScope>
          <component v-else :is="d.comp" />
        </div>
      </div>
    </section>
    </div>

    <div class="stage-nav">
      <button v-if="stageIndex > 0" type="button" class="nav-btn" @click="goPrev">← Previous</button>
      <span class="nav-count">
        <template v-if="totalStages > 1">Stage {{ stageIndex + 1 }} / {{ totalStages }}</template>
        <template v-else>{{ totalDocs }} document{{ totalDocs === 1 ? '' : 's' }}</template>
      </span>
      <button
        v-if="stageIndex >= 0 && stageIndex < totalStages - 1"
        type="button"
        class="nav-btn primary"
        @click="goNext"
      >Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.allot-pkg { color: #111; }

.zoom-bar {
  display: flex;
  justify-content: flex-end;
  width: 794px;
  max-width: 100%;
  margin: 0 auto 18px;
  box-sizing: border-box;
}
.doc-zoom {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f2f4f6;
  border-radius: 10px;
  padding: 2px;
}
.doc-zoom-btn,
.doc-zoom-pct {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 800;
  color: #4b5563;
  border-radius: 8px;
  line-height: 1;
}
.doc-zoom-btn {
  min-width: 36px;
  height: 32px;
  font-size: 15px;
}
.doc-zoom-btn:hover:not(:disabled) {
  background: #fff;
  color: #191f28;
}
.doc-zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.doc-zoom-pct {
  min-width: 52px;
  height: 32px;
  font-size: 12px;
  color: #3182f6;
}
.doc-zoom-pct:hover {
  background: #fff;
}
.paper-zoom {
  width: 794px;
  max-width: 100%;
  margin: 0 auto;
}

.stepper {
  display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 18px;
  padding: 10px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px;
  box-shadow: 0 6px 18px rgba(20, 30, 60, .06); flex-wrap: wrap;
}
.step {
  flex: 1; min-width: 140px; display: flex; align-items: center; gap: 9px; padding: 9px 11px;
  border: 1px solid transparent; border-radius: 10px; background: #f6f8fc; cursor: pointer;
  text-align: left; transition: all .15s;
}
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no {
  flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff;
  font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center;
}
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 12px; color: #1f2d4d; }
.step-txt small { font-size: 10.5px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.stage { width: 794px; max-width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 9px 4px; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 4px 11px; border-radius: 999px; letter-spacing: .02em; }
.stage-t { font-weight: 800; font-size: 14px; color: #1f2d4d; }

.opt-panel {
  display: flex; flex-direction: column; gap: 0;
  border: 1px solid #d7e0f0; border-radius: 12px; background: #fff;
  margin-bottom: 14px; overflow: hidden;
}
.opt-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 14px 16px;
}
.opt-row + .opt-row { border-top: 1px solid #e8eef7; }
.opt-meta { min-width: 0; flex: 1; }
.opt-title { font-size: 13px; font-weight: 800; color: #1f2d4d; line-height: 1.3; }
.opt-desc { margin-top: 2px; font-size: 11.5px; color: #7a869c; line-height: 1.35; }
.opt-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; flex: none; }
.opt-count { font-size: 11.5px; font-weight: 700; color: #8a97b5; }
.opt-on {
  font-size: 11.5px; font-weight: 800; color: #1b64da;
  background: #eaf3ff; border-radius: 999px; padding: 5px 10px;
}

.sg-toolbar {
  display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
  padding: 10px 12px; border: 1px solid #d7e0f0; border-radius: 10px; background: #f7f9fd; margin-bottom: 14px;
}
.sg-toolbar-lbl { font-size: 11px; font-weight: 800; color: #6b7a94; margin-right: 4px; }
.sg-tool {
  border: 1px solid #1B84FF; background: #fff; color: #1B84FF;
  border-radius: 999px; padding: 5px 11px; font-size: 11.5px; font-weight: 700; cursor: pointer;
}
.sg-tool:hover { background: #eaf3ff; }
.sg-tool.muted {
  border-color: #c5cedd; color: #6b7a94;
}
.sg-tool.muted:hover { background: #f3f5f9; }
.empty-hint {
  margin: 0 0 16px; padding: 12px 14px; border-radius: 10px;
  background: #fffaf0; border: 1px dashed #e5c98a;
  font-size: 12.5px; color: #7a5b1a; line-height: 1.45;
}
.empty-hint.soft {
  background: #f7f9fd; border-color: #d7e0f0; color: #6b7a94;
}

.pkg-doc { width: 794px; max-width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1B84FF; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.sg-rm {
  border: none; background: #ffe8e8; color: #c0392b; width: 22px; height: 22px; border-radius: 50%;
  font-size: 14px; line-height: 1; cursor: pointer; font-weight: 800;
}

.simple-frame {
  background: #fff; border: 1px solid #e6ebf3; border-radius: 4px;
  box-shadow: 0 8px 24px rgba(20, 30, 60, .07);
  padding: 48px 56px 56px; min-height: 400px;
}

.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 794px; max-width: 100%; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn:hover:not(:disabled) { background: #f2f6fc; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn.primary:hover:not(:disabled) { background: #1550b8; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
</style>

<style>
.paper:has(.sg-inc-post-pkg) { background: transparent !important; border: none !important; box-shadow: none !important; padding: 0 !important; }
</style>
